import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { CustomButton } from "../../components/CustomButton";
import { useLayout } from "../../context/LayoutContext";
import {
  API_ADD_AI_CREDIT,
  API_GET_AI_CREDIT_SUMMARY,
} from "../../api/company.service";
import "./company.css";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const toCount = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const resolveAiDetails = (rawData, company, companyId) => {
  const fallbackCompanyId = toCount(company?.id || companyId, 0);
  const rawDetails =
    rawData?.data ||
    rawData ||
    company?.aiDetails ||
    company?.aiControlPanel ||
    company?.aiControl ||
    {};
  const details = rawDetails?.data || rawDetails || {};
  const creditSource =
    details?.creditDetails || rawDetails?.creditDetails || {};
  const totalCredit = toCount(creditSource?.credit, 0);
  const usedCredit = toCount(creditSource?.usedCredit, 0);
  const remainingCredit = toCount(
    creditSource?.remainingCredit,
    Math.max(totalCredit - usedCredit, 0),
  );

  return {
    companyId: toCount(details?.companyId, fallbackCompanyId),
    agentCount: toCount(details?.agentCount, 2),
    activeAgentCount: toCount(details?.activeAgentCount, 1),
    creditDetails: {
      companyId: toCount(creditSource?.companyId, fallbackCompanyId),
      credit: totalCredit,
      usedCredit,
      remainingCredit,
      inboundCount: toCount(creditSource?.inboundCount, 0),
      outboundCount: toCount(creditSource?.outboundCount, 0),
      createdAt: creditSource?.createdAt || "",
      updatedAt: creditSource?.updatedAt || "",
    },
  };
};

export const AIControlPanel = ({ company, companyId }) => {
  const { toastMessage } = useLayout();
  const creditPacks = useMemo(
    () => [
      { id: "credit-pack-500", amount: 500, credits: 2000, apiCredit: 2000 },
      { id: "credit-pack-1000", amount: 1000, credits: 5000, apiCredit: 5000 },
      {
        id: "credit-pack-5000",
        amount: 5000,
        credits: 30000,
        apiCredit: 30000,
        featured: true,
      },
    ],
    [],
  );
  const [creditDialogVisible, setCreditDialogVisible] = useState(false);
  const [creditStep, setCreditStep] = useState(1);
  const [selectedPackId, setSelectedPackId] = useState("");
  const [savedPackId, setSavedPackId] = useState("");
  const [aiDetails, setAiDetails] = useState(() =>
    resolveAiDetails(null, company, companyId),
  );
  const [loadingAiDetails, setLoadingAiDetails] = useState(false);
  const [savingCredit, setSavingCredit] = useState(false);

  useEffect(() => {
    setAiDetails((prev) => resolveAiDetails(prev, company, companyId));
  }, [company, companyId]);

  useEffect(() => {
    let alive = true;

    const loadAiDetails = async () => {
      if (!companyId) return;

      setLoadingAiDetails(true);
      try {
        const response = await API_GET_AI_CREDIT_SUMMARY(companyId);
        if (!alive) return;
        setAiDetails(resolveAiDetails(response, company, companyId));
      } catch (error) {
        if (!alive) return;
        toastMessage({
          severity: "error",
          detail:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to load AI credit details.",
        });
      } finally {
        if (alive) setLoadingAiDetails(false);
      }
    };

    loadAiDetails();

    return () => {
      alive = false;
    };
  }, [company, companyId]);

  const creditUsage = useMemo(() => {
    const total = Math.max(aiDetails?.creditDetails?.credit || 0, 1);
    const used = Math.min(aiDetails?.creditDetails?.usedCredit || 0, total);
    return Math.round((used / total) * 100);
  }, [aiDetails]);

  const aiSummaryCards = useMemo(
    () => [
      {
        label: "Total Credit",
        value: aiDetails.creditDetails.credit,
        meta: "AI profile mapped",
      },
      {
        label: "Remaining Credit",
        value: aiDetails.creditDetails.remainingCredit,
        meta: "Available for automation",
      },
      {
        label: "used Credit",
        value: aiDetails.creditDetails.usedCredit,
        meta: "Consumed automation credits",
      },
      {
        label: "Total Agents",
        value: aiDetails.activeAgentCount,
        meta: "AI agents currently active",
      },
    ],
    [aiDetails, companyId],
  );

  const selectedPack = useMemo(
    () => creditPacks.find((pack) => pack.id === selectedPackId) || null,
    [creditPacks, selectedPackId],
  );

  const savedPack = useMemo(
    () => creditPacks.find((pack) => pack.id === savedPackId) || null,
    [creditPacks, savedPackId],
  );

  const projectedCreditBalance = useMemo(() => {
    if (!selectedPack) return aiDetails.creditDetails.remainingCredit;
    return aiDetails.creditDetails.remainingCredit + selectedPack.credits;
  }, [aiDetails, selectedPack]);

  const openCreditDialog = () => {
    setCreditDialogVisible(true);
    setCreditStep(1);
    setSelectedPackId(savedPackId || "");
  };

  const closeCreditDialog = () => {
    setCreditDialogVisible(false);
    setCreditStep(1);
    setSelectedPackId(savedPackId || "");
  };

  const goToConfirmation = () => {
    if (!selectedPack) return;
    setCreditStep(2);
  };

  const saveCreditPlan = () => {
    if (!selectedPack || savingCredit) return;
    const run = async () => {
      try {
        setSavingCredit(true);
        await API_ADD_AI_CREDIT(companyId, { credit: selectedPack.apiCredit });
        const refreshed = await API_GET_AI_CREDIT_SUMMARY(companyId);
        setAiDetails(resolveAiDetails(refreshed, company, companyId));
        setSavedPackId(selectedPack.id);
        setCreditDialogVisible(false);
        setCreditStep(1);
        toastMessage({
          severity: "success",
          detail: "AI credit added successfully.",
        });
      } catch (error) {
        toastMessage({
          severity: "error",
          detail:
            error?.response?.data?.message ||
            error?.message ||
            "Unable to add AI credit.",
        });
      } finally {
        setSavingCredit(false);
      }
    };

    run();
  };

  return (
    <div className="company-tab-panel">
      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">AI control panel</h2>
          </div>
        </div>

        <div className="company-ai-overview-grid">
          {aiSummaryCards.map((item) => (
            <div className="company-ai-summary-card" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <small>{item.meta}</small>
            </div>
          ))}
        </div>

        <div className="company-ai-layout">
          <div className="company-ai-credit-card">
            <div className="company-ai-block-header">
              <div>
                <h3>Credit health</h3>
                <p>Usage and remaining automation balance</p>
              </div>
              <div className="status-pill info">
                <i className="pi pi-bolt" />
                AI Ready
              </div>
            </div>

            <div className="company-ai-credit-meter">
              <div className="company-ai-credit-topline">
                <span>Total {aiDetails.creditDetails.credit}</span>
                <strong>
                  {aiDetails.creditDetails.remainingCredit} used /{" "}
                  {aiDetails.creditDetails.credit} left
                </strong>
              </div>
              <div className="company-ai-progress-track" aria-hidden="true">
                <span style={{ width: `${creditUsage}%` }} />
              </div>
              <div className="company-ai-credit-footline">
                <span>
                  {loadingAiDetails ? "Syncing..." : `${creditUsage}% consumed`}
                </span>
                <span>
                  Updated {formatDateTime(aiDetails.creditDetails.updatedAt)}
                </span>
              </div>
            </div>
          </div>

          <div className="company-ai-side-grid">
            {/* <div className="company-ai-traffic-grid"> */}
              <div className="company-ai-traffic-card">
                <div className="company-ai-traffic-icon">
                  <i className="pi pi-arrow-down-left" />
                </div>
                <div>
                  <span>Inbound Messages</span>
                  <strong>{aiDetails.creditDetails.inboundCount}</strong>
                </div>
              </div>
              <div className="company-ai-traffic-card">
                <div className="company-ai-traffic-icon">
                  <i className="pi pi-arrow-up-right" />
                </div>
                <div>
                  <span>Outbound Messages</span>
                  <strong>{aiDetails.creditDetails.outboundCount}</strong>
                </div>
              </div>
            {/* </div> */}
          </div>
        </div>
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">Add AI credit</h2>
          </div>
        </div>

        <div className="company-ai-add-card">
          <div className="company-ai-add-copy">
            <h3>Top up AI automation credits</h3>
            <p>
              Select a plan, review confirmation details, then save the credit
              request.
            </p>
          </div>

          <div className="company-ai-add-actions">
            <CustomButton
              label="Add AI Credit"
              icon="pi pi-plus"
              onClick={openCreditDialog}
              disabled={loadingAiDetails}
            />
          </div>
        </div>
      </div>

      <Dialog
        header="Top up AI credits"
        visible={creditDialogVisible}
        onHide={closeCreditDialog}
        style={{ width: "min(760px, 94vw)" }}
        className="company-ai-dialog"
        draggable={false}
        resizable={false}
      >
        <div className="company-ai-dialog-body">
          <div className="company-ai-stepper">
            <div
              className={`company-ai-step ${creditStep === 1 ? "active" : "complete"}`}
            >
              <span className="company-ai-step-index">1</span>
              <div>
                <strong>Select Plan</strong>
                <small>Choose a credit pack</small>
              </div>
            </div>
            <div
              className={`company-ai-step ${creditStep === 2 ? "active" : ""}`}
            >
              <span className="company-ai-step-index">2</span>
              <div>
                <strong>Confirmation</strong>
                <small>Review selected plan details</small>
              </div>
            </div>
          </div>

          {creditStep === 1 ? (
            <div className="company-ai-selection-grid">
              {creditPacks.map((pack) => (
                <button
                  type="button"
                  key={pack.id}
                  className={`company-ai-pack-card selectable ${
                    pack.featured ? "featured" : ""
                  } ${selectedPackId === pack.id ? "selected" : ""}`}
                  onClick={() => setSelectedPackId(pack.id)}
                >
                  <div className="company-ai-pack-top">
                    <span className="company-ai-pack-amount">
                      INR {pack.amount}
                    </span>
                    {pack.featured ? (
                      <div className="status-pill info">
                        <i className="pi pi-star-fill" />
                        Best value
                      </div>
                    ) : null}
                  </div>

                  <strong>{pack.credits.toLocaleString()} credits</strong>
                  <small>
                    {Math.round(pack.credits / pack.amount).toLocaleString()}{" "}
                    credits / INR
                  </small>
                </button>
              ))}
            </div>
          ) : (
            <div className="company-ai-confirm-layout">
              <div className="company-ai-confirm-card">
                <div className="company-ai-block-header">
                  <div>
                    <h3>Selected plan</h3>
                    <p>
                      Review the complete credit purchase details before saving.
                    </p>
                  </div>
                  <div className="status-pill info">
                    <i className="pi pi-check-circle" />
                    Ready to save
                  </div>
                </div>

                <div className="company-ai-confirm-grid">
                  {/* <div className="company-ai-confirm-item">
                    <label>Company</label>
                    <p>#{aiDetails.companyId || companyId}</p>
                  </div> */}
                  <div className="company-ai-confirm-item">
                    <label>Plan Price</label>
                    <p>INR {selectedPack?.amount || 0}</p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Credits Added</label>
                    <p>{selectedPack?.credits?.toLocaleString() || 0}</p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Credit Ratio</label>
                    <p>
                      {selectedPack
                        ? `${Math.round(selectedPack.credits / selectedPack.amount).toLocaleString()} credits / INR`
                        : "-"}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Current Remaining</label>
                    <p>
                      {aiDetails.creditDetails.remainingCredit.toLocaleString()}
                    </p>
                  </div>
                  <div className="company-ai-confirm-item">
                    <label>Projected Remaining</label>
                    <p>{projectedCreditBalance.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="company-ai-dialog-footer">
            {creditStep === 2 ? (
              <CustomButton
                label="Back"
                icon="pi pi-arrow-left"
                onClick={() => setCreditStep(1)}
                varient="outline"
              />
            ) : (
              <span />
            )}

            <div className="company-ai-dialog-actions">
              <CustomButton
                label="Cancel"
                onClick={closeCreditDialog}
                varient="outline"
                disabled={savingCredit}
              />
              {creditStep === 1 ? (
                <CustomButton
                  label="Continue"
                  icon="pi pi-arrow-right"
                  onClick={goToConfirmation}
                  disabled={!selectedPack || savingCredit}
                />
              ) : (
                <CustomButton
                  label={savingCredit ? "Saving..." : "Save"}
                  icon="pi pi-check"
                  onClick={saveCreditPlan}
                  disabled={!selectedPack || savingCredit}
                />
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AIControlPanel;
